import React, { useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ExcelTable from 'components/ui/excel-table';
import {
  AllRowsData,
  ECellValueType,
  ExcelTableColDef,
  RowData,
} from 'components/ui/excel-table/types';
import { premiumScheduleApi } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import { useQuery } from 'react-query';
import { validateSettlementExchangeRate } from './form';
import { GetOriginalCurrenciesForAllocationSettlementCurrencyEnum } from 'libs/pinnacle-sdk';
import { uniqAndFilterNil } from 'utils';
import { partition } from 'lodash';

export interface SettlementExchangeRateItem {
  id: number;
  originalCurrency: string;
  settlementCurrency: string;
  settlementExchangeRate?: number;
}

interface SettlementExchangeRateTableProps {
  data: SettlementExchangeRateItem[];
  bankAccountCurrency: string;
  onRateChanged: (data: SettlementExchangeRateItem[]) => void;
  onRateValidated: (isValid: boolean) => void;
  allocationContractTableDataCurrencies: string[];
  sourceBrokerId: string;
}

const SettlementExchangeRateTable: React.FC<
  SettlementExchangeRateTableProps
> = ({
  data,
  bankAccountCurrency,
  onRateValidated,
  onRateChanged,
  sourceBrokerId,
  allocationContractTableDataCurrencies,
}) => {
  const { t } = useTranslation();

  const { data: originalCurrencies, isLoading: isLoadingOriginalCurrencies } =
    useQuery(
      [
        EQueryKeys.ORIGINAL_CURRENCIES_FOR_ALLOCATION,
        sourceBrokerId,
        bankAccountCurrency,
      ],
      () => {
        return premiumScheduleApi.getOriginalCurrenciesForAllocation({
          brokerId: sourceBrokerId,
          settlementCurrency:
            bankAccountCurrency as GetOriginalCurrenciesForAllocationSettlementCurrencyEnum,
        });
      },
      {
        enabled: !!sourceBrokerId && !!bankAccountCurrency,
        refetchOnWindowFocus: false,
      }
    );

  const settlementExchangeRatesWithUnsetCurrencies = useMemo(() => {
    const originalCurrenciesToShow = (originalCurrencies ?? [])
      .map(({ originalCurrency }) => originalCurrency)
      .concat(allocationContractTableDataCurrencies)
      .filter((currency) => currency !== bankAccountCurrency);

    // divide into 2 arrays using partition
    const [availableCurrencies, unavailableCurrencies] = partition(
      uniqAndFilterNil(originalCurrenciesToShow),
      (currency) => data.some((rate) => rate.originalCurrency === currency)
    );
    return [
      ...data.filter((rate) =>
        availableCurrencies.includes(rate.originalCurrency)
      ),
      ...(unavailableCurrencies.map((originalCurrency) => ({
        id: 0,
        originalCurrency: originalCurrency,
        settlementCurrency: bankAccountCurrency,
        settlementExchangeRate: undefined,
      })) ?? []),
    ];
  }, [
    originalCurrencies,
    allocationContractTableDataCurrencies,
    data,
    bankAccountCurrency,
  ]);

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        headerName: t('contractAllocation.originalCurrency'),
        field: 'originalCurrency',
        flex: 1,
        editable: false,
      },
      {
        headerName: t('contractAllocation.settlementCurrency'),
        field: 'settlementCurrency',
        flex: 1,
        editable: false,
      },
      {
        headerName: t('contractAllocation.settlementExchangeRate'),
        field: 'settlementExchangeRate',
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 5,
        editable: true,
      },
    ],
    [t]
  );

  const onErrorChange = useCallback(
    (isError: { strict: boolean; soft: boolean }) =>
      onRateValidated(!isError.strict),
    [onRateValidated]
  );

  const onDataChange = useCallback(
    async (
      _: RowData<SettlementExchangeRateItem>,
      rows: AllRowsData<SettlementExchangeRateItem>
    ) => {
      return onRateChanged?.(rows.data);
    },
    [onRateChanged]
  );

  return (
    <ExcelTable
      data={settlementExchangeRatesWithUnsetCurrencies}
      columnDefs={columnDefs}
      includeAddRow={false}
      includeDeleteCol={false}
      includeIdRow={true}
      onDataChange={onDataChange}
      tableContainerClassName={`h-[${
        Math.max(Math.min(data.length, 20), 10) * 3
      }rem]`}
      loading={isLoadingOriginalCurrencies}
      validateRow={validateSettlementExchangeRate}
      newRowData={{
        id: 0,
        originalCurrency: undefined,
        settlementCurrency: bankAccountCurrency,
        settlementExchangeRate: undefined,
      }}
      onErrorChange={onErrorChange}
      onDeleteRow={onDataChange}
    />
  );
};

export default SettlementExchangeRateTable;
